import { webUrl } from '@/src/utils/linkingUtils';
import { toast } from '@tamagui/toast/v2';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useHeaderHeight } from 'expo-router/react-navigation';
import type { Metadata } from 'expo-server';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ScrollView as RNScrollView } from 'react-native';
import { getTokenValue, Text, View, XStack, YStack } from 'tamagui';

import AccessPassIntro from '@/components/AccessPassIntro';
import AccessPassShowreel from '@/components/AccessPassShowreel';
import Carousel from '@/components/Carousel';
import EventCard from '@/components/EventCard';
import FaqLedger from '@/components/FaqLedger';
import { EventCardsSkeleton } from '@/components/HomeSkeleton';
import MembershipUpsellSection from '@/components/MembershipUpsellSection';
import PartnerMarquee from '@/components/PartnerMarquee';
import PendingPaymentBanner from '@/components/PendingPaymentBanner';
import PolaroidCardStack from '@/components/PolaroidCardStack';
import PSCouponCodeInput from '@/components/PSCouponCodeInput';
import PSHomeSectionEmpty from '@/components/PSHomeSectionEmpty';
import PSHomeSectionError from '@/components/PSHomeSectionError';
import ShowcaseEventCarousel from '@/components/ShowcaseEventCarousel';
import TestimonialNotes, {
  type Testimonial,
} from '@/components/TestimonialNotes';
import PageScrollView from '@/components/ui/PageScrollView';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSText from '@/components/ui/PSText';
import { space } from '@/constants/tokens';
import { asApiError } from '@/src/api/apiError';
import type { ShowcasePayload } from '@/src/api/eventsApi';
import { newIdempotencyKey } from '@/src/api/idempotency';
import membershipsApi, {
  isCouponCodeError,
  type Membership,
} from '@/src/api/membershipsApi';
import { useAccessPassShowcase } from '@/src/hooks/useAccessPassShowcase';
import { useHomePageEvents } from '@/src/hooks/useHomePageEvents';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { usePastMemberships } from '@/src/hooks/usePastMemberships';
import { usePendingMembership } from '@/src/hooks/usePendingMembership';
import { FAQS } from '@/src/lib/faqs';
import {
  deriveMembershipUpsell,
  hasPlansOnOffer,
} from '@/src/lib/membershipUpsell';
import { useInsetStyle } from '@/src/lib/safeAreaInset';
import { useSessionUser } from '@/src/lib/sessionStore';
import { StatusBar } from 'expo-status-bar';

/**
 * The four showcase carousels, in the order the page shows them off. The
 * endpoint only says WHICH events are in each section — the copy is the
 * screen's, the way home's section headings are, so a heading still draws while
 * the fetch is in flight.
 */
const SHOWCASE_SECTIONS: {
  key: keyof ShowcasePayload;
  title: string;
  subtitle: string;
}[] = [
  {
    key: 'uniqueEvents',
    title: 'Unique events',
    subtitle: 'One-offs, not on any listing',
  },
  {
    key: 'inviteOnlyExperiences',
    title: 'Invite-only experiences',
    subtitle: 'On the list, or not in the room',
  },
  {
    key: 'firstTables',
    title: 'First tables',
    subtitle: 'Sit down before the city hears about it',
  },
  {
    key: 'exclusiveGoodies',
    title: 'Exclusive goodies',
    subtitle: 'Members go home with more',
  },
];

const MEMBERSHIPS_TITLE = 'Access Pass · Putting Scene';
const MEMBERSHIPS_DESCRIPTION =
  'First dibs on secret menus, previews, original events, and member-only experiences.';
const MEMBERSHIPS_IMAGE_URL =
  'https://cdn.puttingscene.com/base/APlink-previewbanner.png';

/**
 * SSR-only hook the server calls BEFORE rendering, injecting the result
 * straight into the response's `<head>` — see the fuller explanation on
 * `generateMetadata` in `app/events/[id]/index.tsx`.
 */
export async function generateMetadata(): Promise<Metadata> {
  const pageUrl = webUrl('/memberships');

  return {
    title: MEMBERSHIPS_TITLE,
    description: MEMBERSHIPS_DESCRIPTION,
    openGraph: {
      title: MEMBERSHIPS_TITLE,
      description: MEMBERSHIPS_DESCRIPTION,
      url: pageUrl,
      type: 'website',
      images: [MEMBERSHIPS_IMAGE_URL],
    },
    twitter: {
      card: 'summary_large_image',
      title: MEMBERSHIPS_TITLE,
      description: MEMBERSHIPS_DESCRIPTION,
      images: [MEMBERSHIPS_IMAGE_URL],
    },
  };
}

const TESTIMONIAL_PHOTOS =
  'https://pub-e7747d96903d47c79720752ecf955a6b.r2.dev/ap-landing-page';

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "I thought I knew Bangalore. Turns out I knew the same six restaurants and a couple of bars near my place. Access Pass quietly fixed that. Now my weekends are dinners I'd never have found, a gallery opening on a Tuesday, a wellness class I'd been meaning to try for a year.",
    name: 'Sanithra Raju',
    designation: 'Co-founder, Tijouri',
    avatar: { uri: `${TESTIMONIAL_PHOTOS}/Sanithra%20.jpeg` },
  },
  {
    quote:
      'This city has no shortage of things to do, but having someone consistently surface the experiences actually worth showing up for is a real superpower.',
    name: 'Kartik Parija',
    designation: 'Co-founder, Epic World & Adori Labs',
    avatar: { uri: `${TESTIMONIAL_PHOTOS}/Kartik.jpeg` },
  },
  {
    quote:
      'Putting Scene is my go-to daily companion for discovering Bangalore’s best cultural, food, fitness, and creative events. Truly living up to the hype, Access Pass makes you feel like an insider in Bangalore’s social scene!',
    name: 'Rohan Padha',
    designation: 'Partner at a Consulting Firm',
    avatar: { uri: `${TESTIMONIAL_PHOTOS}/Rohan.jpeg` },
  },
];

/**
 * The hero's pitch line — the CHEAPEST plan's price, which is the screen's call
 * since only it holds the catalog. Same interval rule as the plan card's footer.
 */
const startsAtLine = (plans: Membership[]) => {
  const cheapest = plans.reduce((lowest, plan) =>
    plan.priceWithTax.amountInMinorUnits <
    lowest.priceWithTax.amountInMinorUnits
      ? plan
      : lowest
  );
  return `starts at ${cheapest.priceWithTax.display}${
    cheapest.isRecurring ? `/${cheapest.billingInterval}` : '/-'
  }`;
};

export default function AccessPassLandingPage() {
  const router = useRouter();
  const headerHeight = useHeaderHeight();
  // The plans block sells against the membership in hand, so it needs the user
  // as much as the catalog — see `upsell` below.
  const user = useSessionUser();

  // Three fetches, because they're three endpoints that fail independently and
  // each block below says so on its own: the upcoming rail is `/home`'s Access
  // Pass section, the four carousels are one showcase call, the plans are the
  // catalog.
  const {
    data: home,
    isLoading: isLoadingUpcoming,
    error: upcomingError,
    retry: retryUpcoming,
    refresh: refreshUpcoming,
  } = useHomePageEvents();
  const {
    data: showcase,
    isLoading: isLoadingShowcase,
    error: showcaseError,
    retry: retryShowcase,
    refresh: refreshShowcase,
  } = useAccessPassShowcase();
  const {
    plans,
    isLoading: isLoadingPlans,
    error: plansError,
    refresh: refreshPlans,
  } = useMembershipPlans();
  const pastMemberships = usePastMemberships();

  const [isRefreshing, setIsRefreshing] = useState(false);

  // The coupon row below the plans. The code and its rejection live here because
  // the redeem is one press: preview the code, spend it, hand off to the status
  // screen.
  const [couponCode, setCouponCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  // The COUPON's own rejection, pinned to the field. Everything else toasts —
  // see `handleRedeemCoupon`.
  const [couponError, setCouponError] = useState<string | undefined>();
  // Same resume-after-auth-detour as `pendingPurchase` in
  // `memberships/purchase/confirm.tsx`.
  const [pendingRedeem, setPendingRedeem] = useState(false);

  // Jumping to the All Plans block, measured rather than guessed: every block
  // above it is a fetch whose height we don't know until it lands. `onLayout`
  // reports a y relative to the PARENT, so the block's y is measured inside the
  // body stack and the stack's own y — PageScrollView's top offset, which the
  // frame owns — is added back rather than repeated here.
  const scrollRef = useRef<RNScrollView>(null);
  const bodyTop = useRef(0);
  const [plansTop, setPlansTop] = useState<number | null>(null);
  // Any screen can send a reader straight to the plans with
  // `router.push('/memberships?section=plans')`. The block SLIDES DOWN as the
  // carousels above it land, so the jump isn't one-shot: it re-anchors on every
  // fresh measure until the reader takes the scroll over.
  const { section } = useLocalSearchParams<{ section?: string }>();
  const [isChasingPlans, setIsChasingPlans] = useState(section === 'plans');

  const scrollToPlans = useCallback(
    (animated: boolean) => {
      if (plansTop === null) return;
      // Minus the header: it's transparent, so the top of the viewport sits
      // behind it and the heading would land under the bar.
      scrollRef.current?.scrollTo({
        y: bodyTop.current + plansTop - headerHeight,
        animated,
      });
    },
    [plansTop, headerHeight]
  );

  useEffect(() => {
    const isDataLoading =
      isLoadingUpcoming || isLoadingShowcase || isLoadingPlans;
    if (isChasingPlans && !isDataLoading) scrollToPlans(true);
  }, [
    isChasingPlans,
    scrollToPlans,
    isLoadingUpcoming,
    isLoadingShowcase,
    isLoadingPlans,
  ]);

  useInsetStyle('dark');

  // allSettled, not all: the spinner has to stop even when a leg fails, and each
  // block reports its own failure where it stands.
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.allSettled([
      refreshUpcoming(),
      refreshShowcase(),
      refreshPlans(),
    ]);
    setIsRefreshing(false);
  }, [refreshUpcoming, refreshShowcase, refreshPlans]);

  /**
   * Redeem a typed coupon in one press: preview it, spend it, leave.
   *
   * The preview is what names the plan — the coupon carries its own, which needn't
   * be one of the plans listed above — and it's a read, so a bad code costs
   * nothing. It holds nothing either: `POST /membership` throws every coupon code
   * back at us, which is why both calls share the one catch.
   *
   * Redeeming is a purchase, so both calls refuse an unfinished profile: the
   * press detours into the auth step that fixes it — the same gate as Pay on the
   * confirm screen — and the focus effect below redeems once they're back.
   *
   * A rejection of the CODE (unknown, expired, already used) belongs on the field;
   * everything else — no connection, a membership they already hold (409) — is
   * the app's usual toast.
   */
  const handleRedeemCoupon = useCallback(
    async (code: string) => {
      // The auth flow is a modal over this screen: they land back here with the
      // code still typed.
      if (!user) {
        setPendingRedeem(true);
        router.push({ pathname: '/auth/mobile', params: { next: 'email' } });
        return;
      }
      if (user.areDetailsMissing) {
        setPendingRedeem(true);
        router.push({
          pathname: '/auth/details',
          params: {
            headerCopy: 'Please complete your profile to redeem your coupon',
            next: 'email',
          },
        });
        return;
      }
      if (user.isEmailMissing || user.isOnlyEmailVerificationPending) {
        setPendingRedeem(true);
        router.push({
          pathname: '/auth/email',
          params: {
            headerCopy:
              'Please add and verify your email to redeem your coupon',
          },
        });
        return;
      }

      setIsRedeeming(true);
      setCouponError(undefined);
      try {
        const preview = await membershipsApi.previewCoupon(code);
        const membership = await membershipsApi.createMembership({
          couponCode: preview.couponCode,
          // Fresh per attempt, not per visit: a key replays the body it was
          // first used with, and the code in the field can change between two
          // presses. Nothing double-buys — the server 409s on a membership they
          // hold.
          idempotencyKey: newIdempotencyKey(),
        });

        // The MEMBERSHIP's id is what the status screen polls; the COUPON's plan
        // rides along only so it can name what's being confirmed. `replace`, so
        // there's no back-and-redeem-again.
        router.replace({
          pathname: '/memberships/purchase/status',
          params: { id: membership.id, plan: preview.plan.id },
        });
      } catch (caught) {
        const error = asApiError(caught);
        if (isCouponCodeError(error)) setCouponError(error.message);
        else toast.error(error.message);
        setIsRedeeming(false);
      }
    },
    [user, router]
  );

  // Cleared only once complete, not on any refocus: setting the flag re-runs
  // this while still focused, before the push blurs the screen.
  useFocusEffect(
    useCallback(() => {
      if (!pendingRedeem) return;
      if (user?.isProfileComplete) {
        handleRedeemCoupon(couponCode);
        setPendingRedeem(false);
      }
    }, [pendingRedeem, user?.isProfileComplete, handleRedeemCoupon, couponCode])
  );

  const upcoming = home?.accessPassExclusive ?? [];

  // In flight, every row keeps its heading and swaps its cards for bones. Once
  // the payload lands, a section the server sent nothing for is DROPPED rather
  // than drawn empty — the other three carry the block.
  // What this user may still buy, and what the block should call it. The same
  // pass home's Access Pass block makes — the rules live in one place, so a
  // member sees the same offer on both screens.
  // Held back with the catalog until the history lands, or an ex-member sees the
  // trial offered and then pulled.
  const upsell = deriveMembershipUpsell({
    plans: (pastMemberships && plans) ?? [],
    user,
    pastMemberships: pastMemberships ?? [],
  });
  // Nothing on offer means no block AT ALL: a member on the dearest plan would
  // otherwise get a heading over empty space. The fetch's own states keep it —
  // in flight and failed both have something to say, and an empty catalog is
  // reported rather than silently dropped.
  const pendingMembership = usePendingMembership();
  // A pending payment replaces the plans with the banner — nothing is buyable
  // until it's confirmed or cancelled.
  const showsPlans =
    pendingMembership !== null ||
    !plans?.length ||
    pastMemberships === null ||
    plansError !== null ||
    hasPlansOnOffer(upsell);
  // Waits on the history: until it lands, an ex-member reads as `new`.
  const showsAccessPassIntro =
    pastMemberships !== null &&
    (upsell.state.kind === 'signed-out' || upsell.state.kind === 'new');

  const showcaseRows = SHOWCASE_SECTIONS.map((section) => ({
    ...section,
    events: showcase?.[section.key] ?? [],
  })).filter((row) => isLoadingShowcase || row.events.length > 0);

  const backButton = (
    <PSCircleButton
      onPress={() => (router.canGoBack() ? router.back() : router.push('/'))}
      variant="bare"
    >
      <PSIcon name="arrow-back" color="$borderColor" />
    </PSCircleButton>
  );

  return (
    <YStack flex={1} backgroundColor="$color.brandDark">
      <Head>
        <title>{MEMBERSHIPS_TITLE}</title>
      </Head>
      <Stack.Screen
        options={{
          // Opaque, so the page starts below it — not a transparent bar the
          // content scrolls under. `headerStyle` is a native prop, so the color
          // is the raw token value.
          headerTransparent: false,
          headerStyle: { backgroundColor: getTokenValue('$color.brandDark') },
          headerTitle: () => <></>,
          headerLeft: () => backButton,
          unstable_headerLeftItems: () => [
            {
              type: 'custom' as const,
              element: backButton,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <StatusBar style="light" />
      <PageScrollView
        ref={scrollRef}
        isRefreshing={isRefreshing}
        headerTransparent={false}
        onRefresh={onRefresh}
        onScrollBeginDrag={() => setIsChasingPlans(false)}
        // The coupon field sits below plans/carousels, so the keyboard covers
        // it on focus and nothing in RN lifts it — see the prop's doc.
        keyboardAware
      >
        <YStack
          gap="$space.xxxl"
          mb={'$xxxl'}
          onLayout={(e) => {
            bodyTop.current = e.nativeEvent.layout.y;
          }}
        >
          {/* Full-bleed and pulled up under the transparent header: the negative
              margins cancel PageScrollView's gutter and its top offset, so the
              reel starts at the very top of the page. */}
          <View mx={-space.lg} mt={-space.xl}>
            <AccessPassShowreel
              // No price until the catalog lands. The ellipsis holds the line's
              // height so the hero doesn't jump under the reel; if the fetch
              // FAILED, the plans block below is where that's reported.
              titleString={plans?.length ? startsAtLine(plans) : 'starts at …'}
              ctaTitle="See memberships "
              onPressCta={() => scrollToPlans(true)}
            />
          </View>

          {/* The events a member could book right now — the page's proof that
              the pitch above it is live, so it leads the body. */}
          <YStack gap="$lg">
            {upcomingError && (
              <PSHomeSectionError
                kind={upcomingError.kind}
                onRetry={retryUpcoming}
                isBackgroundDark
              />
            )}
            {isLoadingUpcoming && (
              <Carousel isBackgroundDark>
                <EventCardsSkeleton />
              </Carousel>
            )}
            {upcoming && upcoming.length > 0 && (
              <>
                <PSText.Title color="$textWhite">
                  Coming up for members
                </PSText.Title>
                <Carousel isBackgroundDark>
                  {upcoming.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      panelBgColor="$panelTranslucent"
                      textColor="light"
                      onPress={() => router.push(`/events/${event.id}`)}
                    />
                  ))}
                </Carousel>
              </>
            )}
          </YStack>

          {/* One heading over all four carousels — each carousel titles itself
              from its section, so this is the only copy the screen owns. ONE
              call backs all four, so a failure is reported ONCE here rather than
              four times down the page. */}
          <YStack gap="$xl">
            <PSText.Title color="$textWhite">
              {`What you've missed`}
            </PSText.Title>
            {showcaseError ? (
              <PSHomeSectionError
                kind={showcaseError.kind}
                onRetry={retryShowcase}
                isBackgroundDark
              />
            ) : showcaseRows.length === 0 ? (
              <PSHomeSectionEmpty
                onRefresh={refreshShowcase}
                isBackgroundDark
              />
            ) : (
              <YStack gap={'$space.xxl'}>
                {showcaseRows.map((row) => (
                  <ShowcaseEventCarousel
                    key={row.key}
                    title={row.title}
                    subtitle={row.subtitle}
                    events={row.events}
                    isLoading={isLoadingShowcase}
                  />
                ))}
              </YStack>
            )}
          </YStack>

          {showsPlans && (
            <YStack
              gap={'$space.lg'}
              onLayout={(e) => setPlansTop(e.nativeEvent.layout.y)}
            >
              {pendingMembership ? (
                <PendingPaymentBanner
                  pendingMembership={pendingMembership}
                  onCtaClick={() =>
                    router.push(`/my-memberships/${pendingMembership.id}`)
                  }
                />
              ) : (
                <>
                  {/* No bones while the catalog is in flight: a plan card's height is
                  set by its perk count, which is exactly what we don't have yet, so
                  a placeholder would resize under the reader rather than hold the
                  space. The block sits below five carousels, so there's nothing
                  under the eye to jump. */}
                  {plansError ? (
                    <PSHomeSectionError
                      kind={plansError.kind}
                      onRetry={refreshPlans}
                      isBackgroundDark
                    />
                  ) : !isLoadingPlans && plans?.length === 0 ? (
                    <PSHomeSectionEmpty
                      onRefresh={refreshPlans}
                      isBackgroundDark
                    />
                  ) : (
                    <>
                      <MembershipUpsellSection
                        upsell={upsell}
                        // The page's own heading is right above, and the perk rows
                        // stay: this is the one list written to be compared.
                        withFeatures
                        onSelectMembership={(plan) =>
                          router.push({
                            pathname: '/memberships/purchase/confirm',
                            params: { id: plan.id },
                          })
                        }
                      />
                      <XStack
                        gap={'$space.lg'}
                        my={'$space.lg'}
                        alignItems="center"
                      >
                        <View
                          grow={1}
                          backgroundColor={'$color.panelTranslucent'}
                          height={1}
                        />
                        <Text color={'$color.textWhite'}>or</Text>
                        <View
                          grow={1}
                          backgroundColor={'$color.panelTranslucent'}
                          height={1}
                        />
                      </XStack>

                      <YStack gap={'$space.md'}>
                        <Text
                          fontSize={'$large'}
                          fontWeight={'$semibold'}
                          color={'$color.textWhite'}
                        >
                          Have a coupon code?
                        </Text>
                        <PSCouponCodeInput
                          code={couponCode}
                          redeeming={isRedeeming}
                          error={couponError}
                          onRedeem={handleRedeemCoupon}
                          // Editing invalidates the server's verdict on the old
                          // code.
                          onChangeCode={(next) => {
                            setCouponCode(next);
                            setCouponError(undefined);
                          }}
                        />
                      </YStack>
                    </>
                  )}
                </>
              )}
            </YStack>
          )}

          <PartnerMarquee />
          <TestimonialNotes
            title="Heard around town"
            testimonials={TESTIMONIALS}
          />
          <YStack gap="$space.xxl">
            <PSText.Title color={'$textWhite'}>{`Snapshots`}</PSText.Title>
            <PolaroidCardStack />
          </YStack>

          <FaqLedger faqs={FAQS} />
        </YStack>
      </PageScrollView>
    </YStack>
  );
}
